import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { useConfirm } from 'hooks/useConfirm';
import { useSelector } from 'hooks/useSelector';
import { rbAddReferencePrefix } from 'lib/riskbase';
import { startCase } from 'lodash';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CLEAR_LOCAL_ASSET_CHANGES } from 'store/actions';
import { selectAssetListItemUnmerged } from 'store/assets/selectors';
import { ClearAssetChanges } from 'store/changes/types';
import { AssetError } from 'store/error/types';

interface Props {
  assetError: AssetError;
  assetId: string;
}

export const SyncErrorsAsset: React.FC<Props> = ({ assetError, assetId }) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();

  const asset = useSelector((state) =>
    selectAssetListItemUnmerged(state, assetId),
  )!;

  const clearAssetChanges = useCallback(
    (props: ClearAssetChanges) => {
      confirm({
        confirmText: 'Delete',
        onConfirm: () => dispatch(CLEAR_LOCAL_ASSET_CHANGES(props)),
        text:
          'These changes have NOT been uploaded. ' +
          'Continuing will delete them forever. ' +
          'This cannot be undone.',
        title: 'Delete Changes',
        destructive: true,
      });
    },
    [confirm, dispatch],
  );

  return (
    <Card
      label={`${asset.details.name.toUpperCase()} – ${rbAddReferencePrefix(
        asset.reference.toUpperCase(),
      )}${
        asset.details.custom_reference
          ? ` – ${asset.details.custom_reference}`
          : ''
      }`}
    >
      {assetError.asset ? (
        <CardRow
          labelAlignTop
          label="No Access"
          value={assetError.asset}
          onAction={() => clearAssetChanges({ assetId })}
          actionIcon={ICONS.TrashCanLight}
        />
      ) : (
        Object.entries(assetError).map(([errorId, errorValue]) =>
          errorId !== 'assessments' ? (
            <CardRow
              key={errorId}
              labelAlignTop
              label={startCase(errorId)}
              value={errorValue}
              onAction={() =>
                clearAssetChanges({
                  assetId,
                  assetKeyId: errorId as ClearAssetChanges['assetKeyId'],
                })
              }
              actionIcon={ICONS.TrashCanLight}
            />
          ) : (
            Object.entries(errorValue).map(([subErrorId, subErrorValue]) => (
              <CardRow
                key={subErrorId}
                labelAlignTop
                label="Assessment"
                value={subErrorValue as string}
                onAction={() =>
                  clearAssetChanges({
                    assessmentId: subErrorId,
                    assetId,
                  })
                }
                actionIcon={ICONS.TrashCanLight}
              />
            ))
          ),
        )
      )}
    </Card>
  );
};
